/* eslint-disable react/no-unknown-property */
import React, { Component } from 'react';
import CacheBuster from 'react-cache-buster';
import { Route, Switch } from 'react-router-dom';
import { GlobalStyles } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import version from '../package.json';
import { Theme, Loader } from '@simplybenefits/sb-ui-library';
import MarketplaceOnlySignup from './containers/Signup/MarketplaceOnlySignup';
import ChangeRequestDetailsView from './containers/ChangeRequests/ChangeRequestDetailsView.js';
import PrivateRoute from './PrivateRoute.js';
import * as session from './services/session.js';
import * as events from './services/events.js';
import store from './redux/store.js';
import { loadUserData, loadDependentData, getWhiteLabel, getLocalStorageWhitelabel } from './actions/user.js';
import Notifier from './components/Notifier.js';
import Login from './containers/Login/index.js';
import ForgotPassword from './containers/ForgotPassword/index.js';
import Signup from './containers/Signup/index.js';
import NotFound from './containers/NotFound/index.js';
import InternalError from './containers/InternalError/index.js';
import Dashboard from './containers/Dashboard/index.js';
import Resources from './containers/Resources/index.js';
import Account from './containers/Account/index.js';
import Dependents from './containers/Account/Dependents/index.js';
import Beneficiaries from './containers/Account/Beneficiaries/index.js';
import Trustee from './containers/Account/Trustee.js';
import Claims from './containers/Claims/index.js';
import ClaimSummary from './containers/Claims/ClaimSummary.js';
import Coverage from './containers/Coverage/index.js';
import Card from './containers/Card/index.js';
import SpendingAccounts from './containers/SpendingAccounts/index.js';
import Support from './containers/Support/index.js';
import Usage from './containers/Usage/index.js';
import Notifications from './containers/Notifications/index.js';
import Invoices from './containers/Invoices/index.js';
import Marketplace from './containers/Marketplace/index.js';
import Files from './containers/Files/index.js';
import RequestedChanges from './containers/Account/RequestedChanges/index.js';

class App extends Component {
    state = {
        loading: true,
        theme: false,
        whitelabel: false
    };

    componentDidMount = async () => {
        const token = await session.getToken();
        let whitelabel = null;

        // if we are logged in, let's get the whitelabel using the members actual data and not the domain or the locally stored stuff
        if (token) {
            whitelabel = await store.dispatch(getWhiteLabel(true));
            await this._pullData(token);
        } else {
            // try and get whitelabel data by using the url (no auth required)
            whitelabel = await store.dispatch(getWhiteLabel());

            // if we can't find any whitelabel by domain then look for locally stored whitelabel (they might be using the simply domain)
            if (!whitelabel) {
                whitelabel = await store.dispatch(getLocalStorageWhitelabel());
            }
        }

        // const theme = createMuiTheme(Theme.createTheme(whitelabel && whitelabel.logo ? whitelabel : false));
        const theme = Theme.themeWithWhitelabel(whitelabel);

        this.setState({
            loading: false,
            theme
        });
    };

    _pullData = async (token) => {
        if (token) {
            const isDependent = await session.isDependent();

            if (store.getState().user.meta === null) {
                if (isDependent) {
                    await store.dispatch(loadDependentData());
                } else {
                    await store.dispatch(loadUserData());
                }
            }

            events.record('member_site_load');
        }

        return null;
    };

    render() {
        const globalStyles = {
            '*::-webkit-scrollbar': {
                height: 15,
                width: 15
            },
            '*::-webkit-scrollbar-thumb': {
                backgroundColor: Theme.colors.surface,
                borderRadius: 10,
                boxShadow: 'inset 0 0 2px rgba(0, 0, 0, .5)'
            },
            '*::-webkit-scrollbar-track': {
                backgroundColor: Theme.colors.backgroundGrey,
                borderRadius: 10,
                boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.2)'
            },
            '.MuiDrawer-paper, .MuiPaper-root': {
                zIndex: 'auto !important'
            }
        };

        const styles = {
            main: {
                height: 'auto',
                margin: 0,
                minHeight: '100vh',
                padding: 0,
                position: 'relative'
            }
        };

        if (this.state.loading) {
            return <Loader/>;
        }

        // if no theme is loaded than we are waiting for whitelabel so return nothing
        if (!this.state.theme) {
            return null;
        }

        return (
            <CacheBuster
                currentVersion={version.version}
                isEnabled={ true } // If false, the library is disabled.
                loadingComponent={ <Loader/> } // If not pass, nothing appears at the time of new version check.
            >
                <ThemeProvider theme={this.state.theme}>
                    <CssBaseline />
                    <GlobalStyles styles={globalStyles}/>
                    <main testid="app" style={styles.main}>
                        <Switch>
                            <Route exact path="/login" component={ (props) => <Login {...props}/> } />
                            <Route exact path="/forgotPassword/:token" component={ (props) => <ForgotPassword {...props}/> } />
                            <Route exact path="/signup/:accountID/:userID" component={ (props) => <Signup {...props}/> } />
                            <Route exact path="/signup/:accountID/:userID/:isStripe/:stripeStatus" component={Signup} />
                            <Route exact path="/changeRequests/:changeRequestID/:id/Member/:reviewerID" component={(props) => <ChangeRequestDetailsView {...props}/>} />
                            <Route exact path="/signup/marketplaceOnly/:accountID/:userID" component={ (props) => <MarketplaceOnlySignup {...props}/> } />
                            <Route exact path="/signup/marketplaceOnly/:accountID/:userID/:isStripe/:stripeStatus" component={MarketplaceOnlySignup} />
                            <PrivateRoute exact path="/" component={Dashboard} />
                            <PrivateRoute exact path="/dashboard" component={Dashboard} />
                            <PrivateRoute exact path="/my-account" component={Account} />
                            <PrivateRoute exact path="/my-account/dependents" component={Dependents} />
                            <PrivateRoute exact path="/my-account/beneficiaries" component={Beneficiaries} />
                            <PrivateRoute exact path="/my-account/trustee" component={Trustee} />
                            <PrivateRoute exact path="/my-account/requestedChanges" component={RequestedChanges} />
                            <PrivateRoute exact path="/claims" component={Claims} />
                            <PrivateRoute exact path="/claims/:claimID" component={ClaimSummary} />
                            <PrivateRoute exact path="/coverage" component={Coverage} />
                            <PrivateRoute exact path="/benefits-card" component={Card} />
                            <PrivateRoute exact path="/spending-accounts" component={SpendingAccounts} />
                            <PrivateRoute exact path="/resources" component={Resources} />
                            <PrivateRoute exact path="/support" component={Support} />
                            <PrivateRoute exact path="/usage" component={Usage} />
                            <PrivateRoute exact path="/notifications" component={Notifications} />
                            <PrivateRoute exact path="/invoices" component={Invoices} />
                            <PrivateRoute exact path="/marketplace" component={Marketplace} />
                            <PrivateRoute exact path="/files" component={Files} />
                            <Route path="/error" component={ (props) => <InternalError {...props}/>} />
                            <Route component={ (props) => <NotFound {...props}/>} />
                        </Switch>
                        <Notifier />
                    </main>
                </ThemeProvider>
            </CacheBuster>
        );
    }
}

export default App;